import React, { useEffect, useState } from 'react';
import { Row, Col } from 'reactstrap';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { InView } from 'react-intersection-observer';

import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import { fetchReleased } from 'Redux/ducks/algolia-calls';
import HitCard from 'Components/Cards/HitCard';
import Meta from 'Components/Utility/SEO/Meta';
import Backtotopbutton from 'Components/Utility/Mobile/Backtotopbutton';
import NotFound from 'Components/NotFound/NotFound';

const messages = defineMessages({
  title: {
    id: 'released.seotitle',
    defaultMessage: 'Newly Released | PC Games',
  },
  desc: {
    id: 'released.seodesc',
    defaultMessage: 'Newly released Steam PC games',
  },
});

const Released = () => {
  const [results, setResults] = useState({ hits: [], noHitsFound: false });
  const search = useSelector(state => state.router.location.search);
  const page = Number(getUrlParameter('page', search) || 1);
  const { formatMessage } = useIntl();

  useEffect(() => {
    const fetchHits = async () => {
      const data = await fetchReleased(page - 1);
      setResults({
        hits: data?.hits,
        noHitsFound: !Array.isArray(data?.hits) || data?.hits?.length === 0,
      });
    };

    fetchHits().catch(() => setResults({ hits: [], noHitsFound: true }));
  }, [page]);

  const { hits, noHitsFound } = results;

  if (noHitsFound) return <NotFound />;

  return (
    <main className="container">
      <Meta
        brandTitle
        title={formatMessage(messages.title)}
        description={formatMessage(messages.desc)}
        hits={hits}
        url="recently-added"
        pageType="CollectionPage"
      />
      <InView>
        {({ inView, ref }) =>
          <div ref={ref}>
            <h1 className="pt-5 pb-3">
              <FormattedMessage id="released.header" defaultMessage="New to Fanatical" />
            </h1>

            {!inView &&
              <div className="d-block d-sm-none">
                <Backtotopbutton />
              </div>
            }
          </div>
        }
      </InView>
      <p className="seo-blurb-text mb-4">
        <FormattedMessage id="released.seo.text" defaultMessage="Check out the newest games available on Fanatical, as well as titles coming soon." />
      </p>
      <div className="d-none d-xl-block">
        <Row>
          <Col xl={6}>
            <Row>
              {hits.slice(0, 1).map(hit =>
                <HitCard
                  bigCard
                  key={hit.objectID}
                  hit={hit}
                  xsSize={12}
                  mdSize={12}
                  lgSize={12}
                  coverSizeLg="300"
                  coverSizeXl="550"
                  sizesOverride="(min-width: 1520px) 645px, (min-width: 1200px) 550px, (min-width: 992px) 460px, (min-width: 768px) 340px, (min-width: 576px) 255px, 50vw"
                />,
              )}
            </Row>
          </Col>
          <Col xl={6}>
            <Row>
              {hits.slice(2, 6).map(hit =>
                <HitCard
                  key={hit.objectID}
                  hit={hit}
                  xsSize={6}
                  mdSize={6}
                  lgSize={6}
                />,
              )}
            </Row>
          </Col>
        </Row>
        <Row>
          <Col xl={6}>
            <Row>
              {hits.slice(6, 10).map(hit =>
                <HitCard
                  key={hit.objectID}
                  hit={hit}
                  xsSize={6}
                  mdSize={6}
                  lgSize={6}
                />,
              )}
            </Row>
          </Col>
          <Col xl={6}>
            <Row>
              {hits.slice(1, 2).map(hit =>
                <HitCard
                  bigCard
                  key={hit.objectID}
                  hit={hit}
                  xsSize={12}
                  mdSize={12}
                  lgSize={12}
                  coverSizeLg="300"
                  coverSizeXl="550"
                  sizesOverride="(min-width: 1520px) 645px, (min-width: 1200px) 550px, (min-width: 992px) 460px, (min-width: 768px) 340px, (min-width: 576px) 255px, 50vw"
                />,
              )}
            </Row>
          </Col>
        </Row>
        <Row>
          {hits.slice(10).map(hit =>
            <HitCard
              lazy
              key={hit.objectID}
              hit={hit}
              lgSize={3}
            />,
          )}
        </Row>
      </div>
      <div className="d-block d-xl-none">
        <Row>
          {hits.slice(0, 2).map(hit =>
            <HitCard
              key={hit.objectID}
              hit={hit}
              xsSize={6}
              mdSize={6}
              lgSize={4}
            />,
          )}
          {hits.slice(2, 3).map(hit =>
            <HitCard
              key={hit.objectID}
              hit={hit}
              xsSize={6}
              mdSize={4}
              lgSize={4}
            />,
          )}
          {hits.slice(3).map((hit, i) =>
            <HitCard
              lazy={i >= 8}
              key={hit.objectID}
              hit={hit}
            />,
          )}
        </Row>
      </div>
    </main>
  );
};

export default Released;
